import { useCallback, useEffect, useMemo, useState } from "react";
import { useStartPay } from "../hooks/useStartPay";
import { StartPayError } from "../core/errors";
import { normalizeError } from "../core/normalizeResponse";
import type { StartPaymentRequest } from "../types/payment";
import type { StartPayResponse } from "../types/response";
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";

export type StartPayButtonCallback = (response: StartPayResponse) => void;
export type StartPayButtonClickHandler = (
  event: MouseEvent<HTMLButtonElement>,
  controls: StartPayButtonRenderProps
) => void | Promise<void>;

export type StartPayButtonRenderProps = {
  loading: boolean;
  disabled: boolean;
  lastStatus: StartPayResponse["status"] | null;
  start: () => Promise<StartPayResponse>;
  launchPaymentFlow: () => Promise<StartPayResponse>;
};

export type StartPayButtonProps = StartPaymentRequest &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "disabled" | "onClick"> & {
  disabled?: boolean;
  autoStart?: boolean;
  loadingText?: string;
  successText?: string;
  failureText?: string;
  children?: ReactNode | ((props: StartPayButtonRenderProps) => ReactNode);
  onClick?: StartPayButtonClickHandler;
  onPaymentAuthorized?: StartPayButtonCallback;
  onPaymentDeclined?: StartPayButtonCallback;
  onPaymentPendingReview?: StartPayButtonCallback;
  onPaymentAborted?: StartPayButtonCallback;
  onPaymentException?: StartPayButtonCallback;
  onPaymentSettled?: StartPayButtonCallback;
  onSuccess?: StartPayButtonCallback;
  onFailure?: StartPayButtonCallback;
  onPending?: StartPayButtonCallback;
  onCancel?: StartPayButtonCallback;
  onError?: StartPayButtonCallback;
};

export const StartPayButton = ({
  amount,
  currency,
  orderId,
  customer,
  metadata,
  callbackUrl,
  returnUrl,
  paymentMethods,
  timeout,
  signal,
  disabled = false,
  autoStart = true,
  loadingText = "Processing...",
  successText,
  failureText,
  children = "Start Payment",
  onClick,
  onPaymentAuthorized,
  onPaymentDeclined,
  onPaymentPendingReview,
  onPaymentAborted,
  onPaymentException,
  onPaymentSettled,
  onSuccess,
  onFailure,
  onPending,
  onCancel,
  onError,
  ...buttonProps
}: StartPayButtonProps) => {
  const { startPayment, loading } = useStartPay();
  const [lastStatus, setLastStatus] = useState<StartPayResponse["status"] | null>(null);

  const isDisabled = disabled || loading;
  const input = useMemo(
    () => ({
      amount,
      currency,
      orderId,
      customer,
      metadata,
      callbackUrl,
      returnUrl,
      paymentMethods,
      timeout,
      signal
    }),
    [amount, callbackUrl, currency, customer, metadata, orderId, paymentMethods, returnUrl, signal, timeout]
  );

  useEffect(() => {
    setLastStatus(null);
  }, [orderId]);

  const routeResponse = useCallback(
    (response: StartPayResponse) => {
      setLastStatus(response.status);
      onPaymentSettled?.(response);

      if (response.status === "success") {
        onPaymentAuthorized?.(response);
        onSuccess?.(response);
      } else if (response.status === "failed") {
        onPaymentDeclined?.(response);
        onFailure?.(response);
      } else if (response.status === "pending") {
        onPaymentPendingReview?.(response);
        onPending?.(response);
      } else if (response.status === "cancelled") {
        onPaymentAborted?.(response);
        onCancel?.(response);
      } else {
        onPaymentException?.(response);
        onError?.(response);
      }

      return response;
    },
    [
      onCancel,
      onError,
      onFailure,
      onPaymentAborted,
      onPaymentAuthorized,
      onPaymentDeclined,
      onPaymentException,
      onPaymentPendingReview,
      onPaymentSettled,
      onPending,
      onSuccess
    ]
  );

  const launchPaymentFlow = useCallback(async () => {
    if (isDisabled) {
      return routeResponse(
        normalizeError(
          new StartPayError(
            loading ? "Payment flow is already running" : "Payment button is disabled",
            loading ? "PAYMENT_FLOW_BUSY" : "PAYMENT_BUTTON_DISABLED"
          ),
          orderId
        )
      );
    }

    try {
      return routeResponse(await startPayment(input));
    } catch (error) {
      return routeResponse(normalizeError(error, orderId));
    }
  }, [input, isDisabled, loading, orderId, routeResponse, startPayment]);

  const start = launchPaymentFlow;
  const renderProps = useMemo(
    () => ({ loading, disabled: isDisabled, lastStatus, start, launchPaymentFlow }),
    [isDisabled, lastStatus, launchPaymentFlow, loading, start]
  );

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      await onClick?.(event, renderProps);
      if (autoStart && !event.defaultPrevented) {
        await launchPaymentFlow();
      }
    },
    [autoStart, launchPaymentFlow, onClick, renderProps]
  );

  if (typeof children === "function") {
    return <>{children(renderProps)}</>;
  }

  let label = children;
  if (loading) label = loadingText;
  else if (lastStatus === "success" && successText) label = successText;
  else if ((lastStatus === "failed" || lastStatus === "error") && failureText) label = failureText;

  return (
    <button {...buttonProps} type={buttonProps.type ?? "button"} disabled={isDisabled} onClick={handleClick} aria-busy={loading}>
      <span aria-live="polite">{label}</span>
    </button>
  );
};
